import { api } from './apiClient';
import type {
  TimeSlot,
  SlotAvailabilityResponse,
} from '@/store/slices/candidateSlice';
import { API_ENDPOINTS } from '@/config';

// Interview Slot Booking Types
interface InterviewSlotBookingResponse {
  statusCode: number;
  message: string;
  data: BookInterviewSlotData | null;
  error: string | null;
}

interface BookInterviewSlotData {
  uId: string;
  organizationUId: string;
  candidate: CandidateDetails;
  slotBooking: SlotBookingDetails;
}

export interface InterviewSlotData {
  uId?: string;
  candidateUId?: string;
  jobTitle?: string;
  organizationName?: string;
  interviewDate?: string;
  startTime: string;
  endTime: string;
  durationInMinutes: string;
  interviewType?: string[];
  status?: string;
}

interface InterviewSlotResponse {
  statusCode: number;
  message: string;
  data: InterviewSlotData | null;
  error: string | null;
}

// Instant Interview Response Types
interface InstantInterviewData {
  uId: string;
  candidate: {
    uId: string;
    jobTitle?: string;
    interviewType?: string[];
    organizationUId?: string;
    [key: string]: unknown;
  };
  startTime: string;
  endTime: string;
  durationInMinutes: number;
}

interface InstantInterviewResponse {
  statusCode: number;
  message: string;
  data: InstantInterviewData | null;
  error: string | null;
}

interface CandidateDetails {
  uId: string;
  firstName: string;
  lastName: string;
  email: string;
  jobTitle: string;
  designationOrRole: string;
  profilePhoto?: {
    fileUrl: string;
  };
}

interface SlotBookingDetails {
  uId: string;
  candidateUId: string;
  startTime: string;
  endTime: string;
  interviewDate: string;
  durationInMinutes: string;
  interviewType: string[];
  status: string;
}

export const candidateService = {
  // Slot Booking
  async getAvailableSlots(date: string): Promise<TimeSlot[]> {
    const response = await api.interviewAgent.post<SlotAvailabilityResponse[]>(
      API_ENDPOINTS.interviewSlotBooking.getSlotAvailability,
      { date }
    );

    // Transform API response to TimeSlot format
    return response.map((slot, index) => ({
      uid: `slot-${index}-${slot.startTime}`,
      date: slot.startTime.split('T')[0],
      startTime: slot.startTime,
      endTime: slot.endTime,
      isAvailable: slot.availableInterviews > 0,
      bookedInterviews: slot.bookedInterviews,
      availableInterviews: slot.availableInterviews,
      maxInterviews: slot.maxInterviews,
    }));
  },

  async bookSlot(params: {
    candidateUId: string;
    slotBooking: {
      startTime: string;
      endTime: string;
      interviewDate: string;
    };
  }): Promise<InterviewSlotBookingResponse> {
    return api.interviewAgent.post(
      API_ENDPOINTS.interviewSlotBooking.bookSelectedInterviewSlot,
      params
    );
  },

  // Interview Slot Booking
  async getInterviewSlotByUId(slotUId: string): Promise<InterviewSlotResponse> {
    return api.interviewAgent.post<InterviewSlotResponse>(
      API_ENDPOINTS.interviewSlotBooking.getInterviewSlotByUId,
      {
        uId: slotUId,
      }
    );
  },

  async getInstantInterviewByUId(
    instantInterviewId: string
  ): Promise<InstantInterviewResponse> {
    return api.interviewAgent.post<InstantInterviewResponse>(
      API_ENDPOINTS.interviewSlotBooking.getInstantInterviewByUId,
      {
        uId: instantInterviewId,
      }
    );
  },

  mapInstantInterviewToSlotData(
    instantData: InstantInterviewData
  ): InterviewSlotData {
    return {
      uId: instantData.uId,
      candidateUId: instantData.candidate.uId,
      jobTitle: instantData.candidate.jobTitle,
      organizationName: instantData.candidate.organizationUId,
      startTime: instantData.startTime,
      endTime: instantData.endTime,
      durationInMinutes: String(instantData.durationInMinutes),
      interviewType: instantData.candidate.interviewType,
      status: 'Scheduled',
    };
  },
};
